import type { SurveyAreaCandidate } from 'components/query-map/map-pick-control';
import { formatNumber } from 'utils/number';

/**
 * How far two records' acreages may differ and still be taken as the same tract.
 * The duplicate pairs in production data agree to within a few thousandths of an
 * acre; 1% leaves room for that without admitting a genuinely different shape.
 */
const ACREAGE_TOLERANCE = 0.01;

/**
 * Groups candidates by the tract they describe. `type` is in the key because a
 * PLSS row carries none of the Texas structured fields, so without it every PLSS
 * candidate would collapse into one group with every other.
 *
 * @param {SurveyAreaCandidate} candidate - The candidate to key.
 * @return {string} Its tract key.
 */
const tractKey = (candidate: SurveyAreaCandidate) =>
  [
    candidate.type ?? '',
    candidate.surveyName ?? '',
    candidate.block ?? '',
    candidate.section ?? '',
  ].join('|');

/**
 * Narrows the survey areas under a click to the one that was meant, or hands back
 * the whole set when that can't be decided: most overlap is one tract stored twice
 * (units reference the abstract-less record), but differing sizes are the user's call.
 *
 * @param {SurveyAreaCandidate[]} candidates - Every survey area under the click.
 * @return {SurveyAreaCandidate[]} One candidate when it can be decided,
 * otherwise the candidates that remain in contention.
 */
export const narrowSurveyAreaCandidates = (
  candidates: SurveyAreaCandidate[]
): SurveyAreaCandidate[] => {
  if (candidates.length <= 1) {
    return candidates;
  }

  const keys = new Set(candidates.map(tractKey));
  if (keys.size > 1) {
    return candidates;
  }

  // Without a figure for every candidate there is nothing to compare, so the
  // size test can't be applied and the choice isn't ours to make.
  const acreages = candidates.map((candidate) => candidate.acreage);
  if (acreages.some((acreage) => acreage === undefined || acreage === null)) {
    return candidates;
  }

  const figures = acreages as number[];
  const largest = Math.max(...figures);
  const smallest = Math.min(...figures);
  if (largest <= 0 || (largest - smallest) / largest > ACREAGE_TOLERANCE) {
    return candidates;
  }

  // The tile omits null properties, so a missing abstract arrives as undefined.
  const plain = candidates.filter(
    (candidate) =>
      candidate.abstract === undefined || candidate.abstract === null
  );

  return plain.length === 1 ? plain : candidates;
};

/**
 * How a candidate introduces itself in the overlap picker: the name, whether it
 * is the Section or an Abstract tract (the `abstract` field is null on a
 * Section), and its acreage — the same facts the survey search results carry,
 * so the two ways of choosing an area read alike.
 *
 * @param {SurveyAreaCandidate} candidate - The survey area to describe.
 * @return {string} The picker label.
 */
export const describeSurveyAreaCandidate = (
  candidate: SurveyAreaCandidate
): string => {
  const kind = candidate.abstract
    ? `Abstract ${candidate.abstract}`
    : 'Section';
  const acreage =
    candidate.acreage === undefined
      ? ''
      : ` · ${formatNumber(candidate.acreage, 0, 1)} ac`;
  return `${candidate.name} — ${kind}${acreage}`;
};
